/**
 * Inbound email document schema and paths.
 *
 * The email agent's `pollInbox` function ingests messages from the shared
 * mailbox into `emails/{messageId}` (create-only, keyed on a sanitized RFC-822
 * Message-ID for exactly-once ingestion). `processEmail` then classifies each
 * message, writes the classification back onto the document, and drives the
 * gate. These documents are server-written only (functions use the admin SDK);
 * humans read them indirectly via the reports they produce.
 *
 * Base schema uses {@link FirestoreTimestampSchema} so the admin/ SDK module can
 * extend it with its own timestamp transform.
 */
import {z} from 'zod';
import {FirestoreTimestampSchema} from '../../types';
import type {UniqueStringId} from '../../../types/branded';
import {getBucketDocPath} from '../reportBucket';

// ============================================================================
// Branded id
// ============================================================================

/**
 * Firestore document id of an inbound email. This is a sanitized form of the
 * RFC-822 Message-ID (Firestore ids may not contain `/`); the original header
 * value is preserved in {@link InboundEmailDocument.messageId}.
 */
export type InboundEmailId = UniqueStringId<'InboundEmailId'>;

// ============================================================================
// Enum
// ============================================================================

/**
 * Processing lifecycle of an inbound email. `processEmail` is idempotent and
 * keys off this field: it only acts on `received`, transitions to `processed`
 * (or `error`), and returns early otherwise so Firestore trigger retries do not
 * double-act.
 */
export const InboundEmailStatusSchema = z.enum([
  'received',
  'processing',
  'processed',
  'error',
]);

/** Inferred type for {@link InboundEmailStatusSchema}. */
export type InboundEmailStatus = z.infer<typeof InboundEmailStatusSchema>;

/**
 * The steps of `processEmail`'s classify-and-parse sequence that can fail on an
 * otherwise well-formed email. One member per real throw site, so a recorded
 * stage names where the run actually broke:
 *
 * - `llm_call` — the model request itself rejected (timeout, 5xx, rate limit);
 * - `response_parse` — the response was not parseable JSON;
 * - `schema_validation` — the parsed JSON did not match the classifier's
 *   output schema.
 *
 * These are the only failures `processEmail` degrades: the email was ingested
 * correctly, so it stays visible and hand-labelable in the bulk bucket instead
 * of parking at `error`. Failures outside this sequence (a malformed email
 * document, a converter rejection, any other infrastructure throw) are not
 * classification failures and keep the `error` status.
 */
export const PROCESSING_FAILURE_STAGES = [
  'llm_call',
  'response_parse',
  'schema_validation',
] as const;

/** Zod schema for {@link PROCESSING_FAILURE_STAGES}. */
export const ProcessingFailureStageSchema = z.enum(PROCESSING_FAILURE_STAGES);

/** Inferred type for {@link ProcessingFailureStageSchema}. */
export type ProcessingFailureStage = z.infer<
  typeof ProcessingFailureStageSchema
>;

/**
 * Character cap on a recorded failure reason. The reason is rendered in the
 * reports UI, so it is kept short enough to sit in a table cell and short
 * enough that a runaway provider error message cannot bloat the document.
 */
export const PROCESSING_FAILURE_REASON_MAX_LENGTH = 300;

/**
 * Why the classification of an otherwise well-formed email failed. Written by
 * `processEmail` when it degrades a classification, and read by the reports
 * UI's failure log.
 */
export const ProcessingFailureSchema = z
  .object({
    /** Which step of classify-and-parse threw. */
    stage: ProcessingFailureStageSchema,
    /**
     * Short operator-facing explanation.
     *
     * The writer truncates to {@link PROCESSING_FAILURE_REASON_MAX_LENGTH};
     * the schema deliberately does not, because this converter validates on
     * read and a `.max()` here would reject an over-long document outright
     * rather than showing the operator a clipped reason.
     *
     * **Never contains email content.** The writer builds it from the error's
     * class name, its HTTP status, and schema issue codes/paths only — never
     * from an error message, which can quote the model's output and therefore
     * the email. The body, subject, and sender address are personal data and
     * this field is rendered in the UI.
     */
    reason: z.string(),
    /** Server time the failure was recorded. */
    at: FirestoreTimestampSchema,
  })
  .loose();

/** Inferred type for {@link ProcessingFailureSchema}. */
export type ProcessingFailure = z.infer<typeof ProcessingFailureSchema>;

/**
 * Whether the message came into the shared mailbox (`inbound`, polled from
 * INBOX) or was sent from it (`outbound`, polled from Sent Mail). Outbound
 * messages are not classified/triaged — they only attach to their thread.
 */
export const EmailDirectionSchema = z.enum(['inbound', 'outbound']);

/** Inferred type for {@link EmailDirectionSchema}. */
export type EmailDirection = z.infer<typeof EmailDirectionSchema>;

// ============================================================================
// Document
// ============================================================================

/**
 * Base schema for an inbound email at `emails/{messageId}`.
 *
 * Classification fields (`category`, `extracted`, `courseConfidence`,
 * `actionConfidence`) are `null` until `processEmail` fills them. Exactly one
 * of `bodyInline` / `bodyRef`
 * carries the body: short bodies inline, long bodies spilled to Cloud Storage
 * with a pointer in `bodyRef`.
 */
export const InboundEmailDocumentSchema = z
  .object({
    /** Original RFC-822 `Message-ID` header (source of the document id). */
    messageId: z.string(),
    /** Sender address (RFC-822 `From`), display name and all, as received. */
    from: z.string(),
    /**
     * Bare RFC-5322 addr-spec of the sender (the email address only, with no
     * display name), or `null` when the envelope carried none. This is the
     * canonical reply target — prefer it over {@link InboundEmailDocument.from}
     * (which may include a display name) when addressing a reply.
     *
     * Required (nullable, not optional): every writer stamps it — `pollInbox`
     * from the IMAP envelope, and both reply paths from the resolved sender —
     * so `null` means "the envelope carried no from-address", which is
     * distinguishable from a field nobody set. Keeping it required is what
     * makes that distinction meaningful.
     */
    fromAddress: z.string().nullable(),
    /** Recipient address the message was delivered to. */
    to: z.string().optional(),
    /**
     * RFC-5322 `Cc` recipients captured at ingest, or absent when the message
     * had none.
     */
    cc: z.array(z.string()).optional(),
    /**
     * RFC-3834 `Auto-Submitted` header captured verbatim at ingest (e.g.
     * `auto-replied`), for loop detection. `null` when the header was present
     * but empty; absent when the header was not present.
     */
    autoSubmitted: z.string().nullable().optional(),
    /**
     * `Precedence` header captured verbatim at ingest (e.g. `bulk`, `list`),
     * for loop detection. `null` when present but empty; absent when not
     * present.
     */
    precedence: z.string().nullable().optional(),
    /**
     * RFC-2919 `List-Id` header captured verbatim at ingest, for loop
     * detection (mailing-list traffic). `null` when present but empty; absent
     * when not present.
     */
    listId: z.string().nullable().optional(),
    /**
     * `X-Autoreply` header captured verbatim at ingest, for loop detection.
     * `null` when present but empty; absent when not present.
     */
    xAutoreply: z.string().nullable().optional(),
    /**
     * RFC-8058 `List-Unsubscribe` header captured verbatim at ingest, for loop
     * detection. Its mere presence marks bulk mail — many senders set it
     * without `List-Id` or `Precedence: bulk`, so it is the widest of the bulk
     * signals. `null` when present but empty; absent when not present.
     */
    listUnsubscribe: z.string().nullable().optional(),
    /**
     * `Return-Path` header captured verbatim at ingest, for loop detection.
     * The value of interest is the null envelope sender (`<>`), which marks a
     * bounce/DSN. `null` when present but empty; absent when not present —
     * note the header is added by the receiving MTA, so it is absent on
     * anything not delivered through one (a seeded fixture, for instance).
     */
    returnPath: z.string().nullable().optional(),
    /** Subject line. */
    subject: z.string(),
    /**
     * Direction of the message: `inbound` (received in INBOX) or `outbound`
     * (a reply sent from the shared mailbox, polled from Sent Mail). Optional
     * because the converters validate on read and there is no backfill:
     * messages ingested before Sent Mail polling existed carry no direction at
     * all. Every read site compares against `'outbound'`, so absent reads as
     * inbound — which is what those messages were.
     */
    direction: EmailDirectionSchema.optional(),
    /**
     * Conversation identifier used to group emails into a thread. Ingest takes
     * it from the `messageThreadIndex` entry of the nearest ancestor it
     * recognises, and otherwise derives it — Gmail's `X-GM-THRID` when
     * available, else an RFC-822 root (`references[0]` or the message's own
     * id). `null` only when nothing usable was present. See
     * `resolveIngestThreadId` and `deriveThreadId` in `pollInboxLib`.
     */
    threadId: z.string().nullable(),
    /**
     * Raw RFC-822 `In-Reply-To` header (the immediate parent's Message-ID), or
     * `null` when absent. Kept for provider-agnostic thread reconstruction.
     * Optional because the converters validate on read and there is no
     * backfill: documents ingested before this field existed must stay
     * readable.
     */
    inReplyTo: z.string().nullable().optional(),
    /**
     * RFC-822 `References` header chain (ancestry, oldest first), or `null`
     * when absent. Kept for provider-agnostic thread reconstruction. Optional
     * for the same read-compatibility reason as `inReplyTo`.
     */
    references: z.array(z.string()).nullable().optional(),
    /**
     * Course bucket this email has been routed to, or `null` while it is still
     * in the bulk (`_uncategorized`) bucket. Set by `processEmail` when it
     * assigns a course with confidence.
     */
    courseId: z.string().nullable(),
    /** Server time the message was ingested. */
    receivedAt: FirestoreTimestampSchema,
    /** Inline plain-text body for short messages, or `null` when spilled. */
    bodyInline: z.string().nullable(),
    /**
     * Inline HTML body captured from the message's `text/html` MIME part, or
     * `null` when the message had no HTML part — a plain-text message is
     * ordinary, so renderers MUST fall back to
     * {@link InboundEmailDocument.bodyInline} when this is `null`. Optional
     * because the converters validate on read and there is no backfill:
     * documents ingested before this field existed must stay readable, and they
     * carry the key not at all rather than as `null`. Every writer still sets
     * it; absent means "ingested too early to know", which renderers treat the
     * same as `null` (they guard with `isNullOrUndefined`, so both fall back to
     * the plain-text body). This is attacker-controlled input — every render
     * path must sanitize and sandbox it (see the reports view modals /
     * `HtmlSandbox`). Truncated at ingest to its own cap
     * (`INBOX_BODY_HTML_INLINE_MAX_CHARS` in `pollInboxLib`); oversized HTML is
     * cut, never spilled to Storage in v1.
     */
    bodyHtmlInline: z.string().nullable().optional(),
    /** Cloud Storage path for long bodies, or `null` when inline. */
    bodyRef: z.string().nullable(),
    /**
     * Inquiry-type slug assigned by the LLM (see the `inquiryType` taxonomy), or
     * `null` before processing. Stored as a plain string — and normalized to an
     * active slug at classification time — so reads stay robust as the taxonomy
     * evolves.
     */
    category: z.string().nullable(),
    /** Category-specific extracted fields, or `null` before processing. */
    extracted: z.record(z.string(), z.unknown()).nullable(),
    /**
     * Classifier confidence that `courseId` is correct, in `[0, 1]` (both
     * bounds inclusive), or `null` before processing. Course routing is gated
     * on this.
     */
    courseConfidence: z.number().min(0).max(1).nullable(),
    /**
     * Classifier confidence in the proposed action, in `[0, 1]` (both bounds
     * inclusive), or `null` before processing. The gate is driven by this.
     */
    actionConfidence: z.number().min(0).max(1).nullable(),
    /**
     * Classifier confidence that `category` is correct, in `[0, 1]` (both
     * bounds inclusive), or `null` before processing. Independent of
     * `actionConfidence`; gates the auto-reply path (`decideAutoReply`).
     */
    categoryConfidence: z.number().min(0).max(1).nullable().optional(),
    /** Processing lifecycle status. */
    status: InboundEmailStatusSchema,
    /**
     * Id of the report this email produced (`reports/{reportId}`), or `null`
     * before processing. Every processed email links to exactly one report.
     */
    linkedReportId: z.string().nullable(),
    /**
     * Why classification failed, when it did. Present only on emails whose
     * classification degraded (see {@link ProcessingFailureSchema}); the email
     * still carries `status: 'processed'`, because it was ingested correctly
     * and is triageable — it simply has no useful classification.
     *
     * **Optional, and it must stay optional.** Both converters here are built
     * with `validateOnRead`, and every email written before this field existed
     * lacks the key; requiring it would make the converter throw a `ZodError`
     * inside the reports page's render path for those documents.
     */
    processingFailure: ProcessingFailureSchema.optional(),
    /**
     * For an `outbound` message authored by a staff member through the reports
     * UI (a manual reply), the Firebase `uid` of the sender. `null`/absent for
     * inbound mail and for outbound mail ingested from the Sent mailbox (which
     * carries no in-app author). Recorded for audit — who replied from the app.
     */
    sentByUid: z.string().nullable().optional(),
    /**
     * For a manual reply, the id of the `mail/{mailDocId}` document created to
     * send it (whose extension-managed `delivery` field carries the send
     * status). Links this outbound copy to its delivery record so a failed send
     * can be surfaced back onto the thread. `null`/absent for inbound mail and
     * for outbound mail ingested from the Sent mailbox.
     */
    mailDocId: z.string().nullable().optional(),
    /**
     * The RFC-822 `Message-ID` the relay actually stamped on an outbound copy,
     * captured from the Trigger Email extension's `delivery.info.messageId`
     * after the send (see `captureReplyMessageId` in `@cip/functions`).
     *
     * This exists separately from {@link InboundEmailDocument.messageId}
     * because a reply's real id is not knowable at write time: the reply path
     * mints the document itself, so `messageId` on an outbound copy is only
     * its Firestore doc id standing in for a header we do not yet have. For
     * IMAP-ingested mail `messageId` *is* the header, and this field is
     * absent.
     *
     * `null`/absent for ingested mail and for anything sent before capture
     * existed. Optional for the same read-compatibility reason as
     * `inReplyTo`.
     */
    providerMessageId: z.string().nullable().optional(),
  })
  .loose();

/** Inferred type for {@link InboundEmailDocumentSchema}. */
export type InboundEmailDocument = z.infer<typeof InboundEmailDocumentSchema>;

// ============================================================================
// Paths
// ============================================================================

/** Subcollection (under a bucket) holding that bucket's inbound emails. */
export const BUCKET_EMAILS_SUBCOLLECTION = 'emails' as const;

/**
 * Emails subcollection path for a bucket.
 *
 * @param args.bucketId - A course id, or the bulk-bucket id
 *   (`UNCATEGORIZED_BUCKET_ID` from `../report`).
 * @returns Firestore collection path `reports/{bucketId}/emails`.
 * @throws Never — pure string construction.
 */
export function getBucketEmailsCollectionPath({
  bucketId,
}: {
  bucketId: string;
}): string {
  return `${getBucketDocPath({bucketId})}/${BUCKET_EMAILS_SUBCOLLECTION}`;
}

/**
 * Single inbound-email document path.
 *
 * @param args.bucketId - The bucket the email lives in (course id or the bulk
 *   bucket id).
 * @param args.emailId - The sanitized Message-ID used as the document id.
 * @returns Firestore doc path `reports/{bucketId}/emails/{emailId}`.
 * @throws Never — pure string construction.
 */
export function getInboundEmailDocumentPath({
  bucketId,
  emailId,
}: {
  bucketId: string;
  emailId: string;
}): string {
  return `${getBucketEmailsCollectionPath({bucketId})}/${emailId}`;
}
